import { useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import {
  geocodeAddress,
  type GeocodeHit,
} from "../../lib/geocodeAddress";
import {
  findDealOwnershipHints,
  type DealOwnershipHint,
} from "../../lib/dealOwnershipHints";
import {
  resolveCanvasPinForLead,
  deleteOrphanPin,
} from "../../lib/attachCanvasPin";

/**
 * ManualEntryModal — shared between AlexASMView (ASM-initiated), the Home page
 * (RM/TL/exec-initiated) and the Referrals board. Always inserts the deal at
 * `pending_asm_review` so Alex reviews and approves before it hits the RM's
 * appointment queue — unless the owner takes the executive path (Oct 22,
 * 2026): when the owner holds a role listed in finance_settings
 * `asm_review_bypass_roles` (Chad, Jennifer, Robert, Greg), the database
 * trigger trg_deals_exec_path_bypass moves the deal to appointment_received in
 * the same transaction, so it never appears in the ASM's queue. The modal only
 * asks `deals_exec_path_owner(owner)` so its copy says where the lead goes; the
 * server decides.
 *
 * Props:
 *  onClose        — called when the modal should be dismissed
 *  onCreated      — called after a successful insert (close + reload)
 *  rosterMembers  — full active roster with role, used to build the RM dropdown
 *  actorId        — the logged-in user's team_roster id (for the activity log)
 *  defaultOwnerId — if provided, pre-selects that RM and locks the field
 *                   (used when an RM opens the modal — they own the deal)
 */

export type RosterMember = { id: string; full_name: string; role: string };

export type ManualEntryPrefill = Partial<{
  homeowner_name: string;
  homeowner_phone: string;
  homeowner_email: string;
  property_street: string;
  property_city: string;
  property_state: string;
  property_zip: string;
  appointment_notes: string;
}>;

interface ManualEntryModalProps {
  onClose: () => void;
  /** Receives the new deal's id so callers can link it to something (e.g. the
   *  Referrals board links the referral to the deal it became). */
  onCreated: (dealId?: string) => void;
  rosterMembers: RosterMember[];
  actorId: string | null;
  defaultOwnerId?: string | null;
  /** Seeds the form. Used by the Referrals board so an RM never re-types the
   *  name/address/phone the customer already gave us. */
  prefill?: ManualEntryPrefill;
}

// Roles that appear in the "Assign to RM" dropdown.
// restore_manager = the actual DB enum value for field RMs.
const RM_ROLES = new Set([
  "restore_manager",
  "team_lead",
  "asm",
  "sales_director",
  "admin",
  "roof_director",
  "director_of_operations",
  "roof_team_manager", // Oct 22, 2026: Robert takes the executive path
]);

export default function ManualEntryModal({
  onClose,
  onCreated,
  rosterMembers,
  actorId,
  defaultOwnerId = null,
  prefill,
}: ManualEntryModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const dupesWarningRef = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);

  // Geocode + dedup state. Both are set on the first save attempt; the user
  // must explicitly confirm to proceed past either gate. The dedup check runs
  // through the find_deal_ownership_hints RPC (SECURITY DEFINER) so it sees
  // OTHER reps' deals too — a plain deals query is RLS-scoped to the RM's own
  // deals and would miss exactly the duplicates that matter.
  const [geocoded, setGeocoded] = useState<GeocodeHit | null>(null);
  const [dupes, setDupes] = useState<DealOwnershipHint[] | null>(null);
  const [confirmedDupeOverride, setConfirmedDupeOverride] = useState(false);

  // Bumped every time `resetGates()` fires. The in-flight geocode/dedup
  // captures this on entry and bails on resolve if the user edited the
  // address mid-flight — prevents stale coords from being stamped on a
  // different address.
  const gateReqId = useRef(0);

  // Only show field reps in the dropdown
  const fieldReps = rosterMembers
    .filter((m) => RM_ROLES.has(m.role))
    .sort((a, b) => a.full_name.localeCompare(b.full_name));

  const ownerLocked = !!defaultOwnerId;

  const [form, setForm] = useState({
    homeowner_name: prefill?.homeowner_name ?? "",
    homeowner_phone: prefill?.homeowner_phone ?? "",
    // Oct 16, 2026: email was never asked for here, so Log-a-Lead deals had
    // no email until finance needed one. Optional, format-checked on save.
    homeowner_email: prefill?.homeowner_email ?? "",
    property_street: prefill?.property_street ?? "",
    property_city: prefill?.property_city ?? "",
    property_state: prefill?.property_state || "GA",
    property_zip: prefill?.property_zip ?? "",
    insurance_company: "",
    owner_id: defaultOwnerId ?? "",
    appointment_notes: prefill?.appointment_notes ?? "",
  });

  // Executive path (Oct 22, 2026): true when the selected owner's role skips
  // ASM review. Read from the database so the roles list lives in one place
  // (finance_settings.asm_review_bypass_roles); null while unknown or on a
  // failed read, which falls back to the ASM-review copy.
  const [execPath, setExecPath] = useState<boolean | null>(null);
  useEffect(() => {
    const ownerId = form.owner_id;
    if (!ownerId) {
      setExecPath(null);
      return;
    }
    let cancelled = false;
    supabase
      .rpc("deals_exec_path_owner", { p_owner: ownerId })
      .then(({ data, error }) => {
        if (cancelled) return;
        setExecPath(error ? null : data === true);
      });
    return () => {
      cancelled = true;
    };
  }, [form.owner_id]);

  // Phone included: it's now part of the dedup match, so editing it must
  // re-run the gate too (re-geocoding on a phone edit is a cheap no-op).
  const ADDRESS_KEYS = new Set<keyof typeof form>([
    "property_street",
    "property_city",
    "property_state",
    "property_zip",
    "homeowner_phone",
  ]);

  const set =
    (k: keyof typeof form) =>
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      setForm((f) => ({ ...f, [k]: e.target.value }));
      // Editing any part of the address invalidates the previous geocode +
      // dedup result — force them to re-run on the next save.
      if (ADDRESS_KEYS.has(k)) resetGates();
    };

  /**
   * Save flow runs in up to three passes:
   *   Pass 1: validate inputs → geocode the address. If no coords resolvable,
   *           stop with a friendly error (no deal row created).
   *   Pass 2: dedup check for active deals at the same address. If any exist,
   *           surface them and require the user to tap "Create anyway" (sets
   *           `confirmedDupeOverride`) before continuing.
   *   Pass 3: actual INSERT into `deals` with property_lat/lon stamped from
   *           the geocode hit, then activity_log row noting the dup override
   *           if applicable.
   *
   * Re-entry: each tap of "Add to CRM" re-runs from the current state — once
   * geocoded + (optionally) override-confirmed, the next tap performs the
   * INSERT. Editing the address resets both gates.
   */
  async function doSave() {
    if (!form.homeowner_name.trim()) {
      setFormErr("Homeowner name is required.");
      return;
    }
    if (!form.property_street.trim()) {
      setFormErr("Street address is required.");
      return;
    }
    if (!form.property_city.trim()) {
      setFormErr("City is required.");
      return;
    }
    if (!form.owner_id) {
      setFormErr("Please select an RM.");
      return;
    }
    const emailTrimmed = form.homeowner_email.trim();
    if (emailTrimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailTrimmed)) {
      setFormErr("That email doesn't look right — check it or leave it blank.");
      return;
    }

    setFormErr(null);

    // Pass 1: geocode (cached after the first successful resolve)
    let hit = geocoded;
    if (!hit) {
      const myReq = gateReqId.current;
      setResolving(true);
      try {
        hit = await geocodeAddress(
          form.property_street,
          form.property_city,
          form.property_state,
          form.property_zip,
        );
      } catch {
        hit = null;
      } finally {
        // Only clear `resolving` if this is still the latest request —
        // otherwise a newer save is in flight and owns the loading state.
        if (myReq === gateReqId.current) setResolving(false);
      }
      // Address was edited mid-resolve — discard this result silently.
      // resetGates has already nulled `geocoded`; the next save retries.
      if (myReq !== gateReqId.current) return;
      if (!hit) {
        setFormErr(
          "Couldn't find this address on a map. Double-check the spelling, " +
            "ZIP, and city — every manually-logged deal needs coordinates so " +
            "the drone team can schedule it.",
        );
        return;
      }
      setGeocoded(hit);
    }

    // Pass 2: dedup check (only runs once, until address/phone edited).
    // Cross-RLS via RPC — matches street-number + street-name (loose, so
    // "Drive" finds "Dr") and/or the homeowner phone, live deals only.
    if (dupes === null) {
      const myReq = gateReqId.current;
      const matches = await findDealOwnershipHints({
        street: form.property_street,
        city: form.property_city,
        phone: form.homeowner_phone,
      });
      if (myReq !== gateReqId.current) return; // address changed mid-flight
      setDupes(matches);
      if (matches.length > 0 && !confirmedDupeOverride) {
        // Surface the dupe(s); next paint scrolls them into view so the
        // user actually sees the amber banner above the footer button.
        requestAnimationFrame(() => {
          dupesWarningRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        });
        return;
      }
    }
    if (
      dupes !== null &&
      dupes.length > 0 &&
      !confirmedDupeOverride
    ) {
      return;
    }

    // Pass 3: actual insert
    setSaving(true);
    try {
      const notes = ["📝 Manual entry", form.appointment_notes.trim()]
        .filter(Boolean)
        .join(" — ");
      const reviewNote = execPath
        ? "Executive path, no ASM review."
        : "Pending ASM review.";

      // Resolve a canvas pin FIRST (reuse one on the parcel, else create one)
      // so its id rides along on the deal INSERT. The advance trigger only
      // fires usefully on INSERT — a later knock_pin_id UPDATE with an
      // unchanged stage is a no-op. Best-effort: null pin → lead still saves.
      const pin = await resolveCanvasPinForLead({
        lat: hit.lat,
        lon: hit.lon,
        street: form.property_street,
        city: form.property_city,
        state: form.property_state,
        zip: form.property_zip || null,
        homeownerName: form.homeowner_name,
        actorId,
      });

      let deal: { id: string } | null = null;
      try {
        const { data, error } = await supabase
          .from("deals")
          .insert({
            homeowner_name: form.homeowner_name.trim(),
            homeowner_phone: form.homeowner_phone.trim() || null,
            homeowner_email: form.homeowner_email.trim().toLowerCase() || null,
            property_street: form.property_street.trim(),
            property_city: form.property_city.trim(),
            property_state: (form.property_state.trim() || "GA").toUpperCase(),
            property_zip: form.property_zip.trim() || null,
            property_lat: hit.lat,
            property_lon: hit.lon,
            insurance_company: form.insurance_company.trim() || null,
            owner_id: form.owner_id,
            stage: "pending_asm_review",
            phase: "leads",
            appointment_notes: notes,
            // Linking on INSERT auto-advances the pin to appt_scheduled and
            // back-links knock_pins.deal_id via trg_canvas_advance_pin_from_deal.
            knock_pin_id: pin?.pinId ?? null,
          })
          .select("id")
          .single();
        if (error) throw error;
        deal = data;
      } catch (insertErr) {
        // Don't strand a freshly-created pin if the deal insert failed.
        if (pin && !pin.reused) await deleteOrphanPin(pin.pinId);
        throw insertErr;
      }

      if (deal?.id) {
        const canvasNote = pin
          ? pin.reused
            ? " Linked to the existing canvas pin at this address."
            : " Added to the canvas map."
          : "";

        await supabase.from("activity_log").insert({
          deal_id: deal.id,
          activity_type: "note",
          title: "Manual Entry",
          description:
            (confirmedDupeOverride
              ? `Deal created manually (RM confirmed duplicate-override; ${dupes?.length ?? 0} active deal(s) already at this address). ${reviewNote} Geocoded via ${hit.source}.`
              : `Deal created manually — ${reviewNote} Geocoded via ${hit.source}.`) +
            canvasNote,
          metadata: {
            source: "manual_entry",
            actor_id: actorId,
            exec_path: execPath === true,
            geocode_source: hit.source,
            geocode_lat: hit.lat,
            geocode_lon: hit.lon,
            duplicate_override: confirmedDupeOverride,
            duplicate_of: confirmedDupeOverride
              ? dupes?.map((d) => d.id) ?? []
              : null,
            canvas_pin_id: pin?.pinId ?? null,
            canvas_pin_reused: pin?.reused ?? null,
            canvas_parcel_id: pin?.parcelId ?? null,
          },
        });
      }

      onCreated(deal?.id);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === "object" && err !== null && "message" in err
            ? String((err as { message: unknown }).message)
            : null;
      setFormErr(message ?? "Failed to create deal.");
    } finally {
      setSaving(false);
    }
  }

  /** Editing the address resets both gates so they run again on next save.
   *  Bumps `gateReqId` first so any in-flight geocode/dedup self-aborts on
   *  resolve (see Pass 1 + Pass 2). */
  function resetGates() {
    gateReqId.current += 1;
    if (geocoded || dupes !== null || confirmedDupeOverride) {
      setGeocoded(null);
      setDupes(null);
      setConfirmedDupeOverride(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    doSave();
  }

  function handleOverlayClick(e: React.MouseEvent) {
    if (e.target === overlayRef.current) onClose();
  }

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-0 sm:px-4"
    >
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-slate-100">
          <div>
            <h2 className="text-sm font-semibold text-bardin-ink">
              Manual Lead Entry
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {execPath
                ? "Executive path — skips ASM review and goes straight to drone scheduling."
                : "Goes to ASM Review — Alex approves before it hits your queue."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-lg leading-none"
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="overflow-y-auto px-5 py-4 space-y-3 flex-1"
        >
          {/* Homeowner */}
          <div>
            <label className="block text-[11px] font-medium text-slate-500 mb-1">
              Homeowner Name *
            </label>
            <input
              value={form.homeowner_name}
              onChange={set("homeowner_name")}
              placeholder="Jane Smith"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bardin-orange/30"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-[11px] font-medium text-slate-500 mb-1">
              Phone
            </label>
            <input
              value={form.homeowner_phone}
              onChange={set("homeowner_phone")}
              placeholder="(770) 555-1234"
              type="tel"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bardin-orange/30"
            />
          </div>

          <div>
            <label className="block text-[11px] font-medium text-slate-500 mb-1">
              Email <span className="text-slate-400 font-normal">(for the agreement, invoice and warranty)</span>
            </label>
            <input
              value={form.homeowner_email}
              onChange={set("homeowner_email")}
              placeholder="jane@example.com"
              type="email"
              inputMode="email"
              autoComplete="off"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bardin-orange/30"
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-[11px] font-medium text-slate-500 mb-1">
              Street Address *
            </label>
            <input
              value={form.property_street}
              onChange={set("property_street")}
              placeholder="123 Oak Lane"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bardin-orange/30"
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1">
              <label className="block text-[11px] font-medium text-slate-500 mb-1">
                City *
              </label>
              <input
                value={form.property_city}
                onChange={set("property_city")}
                placeholder="Marietta"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bardin-orange/30"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-[11px] font-medium text-slate-500 mb-1">
                State
              </label>
              <input
                value={form.property_state}
                onChange={set("property_state")}
                placeholder="GA"
                maxLength={2}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bardin-orange/30 uppercase"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-[11px] font-medium text-slate-500 mb-1">
                ZIP
              </label>
              <input
                value={form.property_zip}
                onChange={set("property_zip")}
                placeholder="30060"
                maxLength={10}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bardin-orange/30"
              />
            </div>
          </div>

          {/* Insurance */}
          <div>
            <label className="block text-[11px] font-medium text-slate-500 mb-1">
              Insurance Company
            </label>
            <input
              value={form.insurance_company}
              onChange={set("insurance_company")}
              placeholder="State Farm, Allstate, etc."
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bardin-orange/30"
            />
          </div>

          {/* RM — locked when the RM opens the modal themselves */}
          <div>
            <label className="block text-[11px] font-medium text-slate-500 mb-1">
              Assign to RM *
            </label>
            {ownerLocked ? (
              <div className="w-full border border-slate-200 bg-slate-50 rounded-lg px-3 py-2 text-sm text-slate-600">
                {fieldReps.find((r) => r.id === form.owner_id)?.full_name ??
                  "You"}
              </div>
            ) : (
              <select
                value={form.owner_id}
                onChange={set("owner_id")}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bardin-orange/30 bg-white"
              >
                <option value="">— select rep —</option>
                {fieldReps.map((rep) => (
                  <option key={rep.id} value={rep.id}>
                    {rep.full_name}
                  </option>
                ))}
              </select>
            )}
            {execPath && (
              <p className="mt-1 text-[11px] text-emerald-700">
                Executive lead — no ASM review. Saving sends the homeowner the
                confirmation text right away.
              </p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[11px] font-medium text-slate-500 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={form.appointment_notes}
              onChange={set("appointment_notes")}
              placeholder="Storm date, referral source, RM's screenshot context…"
              rows={2}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bardin-orange/30 resize-none"
            />
          </div>

          {/* Dupe warning — surfaces when the dedup check finds active deal(s)
              at the same address. User can review and either cancel or
              explicitly override. */}
          {dupes !== null && dupes.length > 0 && !confirmedDupeOverride && (
            <div
              ref={dupesWarningRef}
              className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-900 space-y-1.5"
            >
              <div className="font-semibold">
                ⚠ This lead already exists in the CRM
              </div>
              <ul className="space-y-1.5">
                {dupes.slice(0, 3).map((d) => {
                  return (
                    <li key={d.id} className="rounded-md bg-white/70 border border-amber-200 px-2 py-1.5">
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-medium">
                          {d.homeowner_name ?? "(no name)"}
                        </span>
                        <span className="text-amber-700">
                          · {d.property_street ?? ""} · {(d.stage ?? "").replace(/_/g, " ")}
                        </span>
                      </div>
                      {d.owner_name ? (
                        <div className="mt-0.5 text-amber-800">
                          Owned by <strong>{d.owner_name}</strong>
                          {d.split_name && d.split_name !== d.owner_name ? (
                            <> (split: {d.split_name})</>
                          ) : null}
                          {d.owner_phone ? (
                            <>
                              {" — "}
                              <a href={`tel:${d.owner_phone}`} className="underline font-semibold">
                                {d.owner_phone}
                              </a>
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
              <div className="text-amber-700">
                Working this one together? <strong>Reach out to the owning RM and
                ask them to add you as the split RM</strong> on the existing deal —
                that gives you full access. Don't create a second deal for the
                same job: duplicates get flagged for your 1-on-1. Only proceed if
                this is genuinely a different property or unit.
              </div>
              <button
                type="button"
                onClick={() => setConfirmedDupeOverride(true)}
                className="mt-1 px-2.5 py-1 rounded-md bg-amber-600 text-white text-[11px] font-semibold hover:bg-amber-700 transition"
              >
                This is a different job — create anyway →
              </button>
            </div>
          )}

          {/* Geocode hint — shows once the address has been resolved. */}
          {geocoded && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-[11px] text-emerald-800">
              ✓ Address resolved via {geocoded.source} ({geocoded.lat.toFixed(5)},{" "}
              {geocoded.lon.toFixed(5)}) · will be pinned on the canvas
            </div>
          )}

          {formErr && (
            <div className="bg-red-50 text-red-700 text-xs rounded-lg px-3 py-2">
              {formErr}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-100 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={doSave}
            disabled={saving || resolving}
            className="flex-1 py-2 rounded-lg bg-bardin-orange text-white text-sm font-semibold hover:opacity-90 transition disabled:opacity-50"
          >
            {resolving
              ? "Finding address…"
              : saving
                ? "Creating…"
                : dupes !== null && dupes.length > 0 && !confirmedDupeOverride
                  ? "Review duplicates above"
                  : "Add to CRM"}
          </button>
        </div>
      </div>
    </div>
  );
}
